import { useState, useEffect } from 'react';

// Detect Capacitor runtime
const isCapacitor = () =>
  typeof window !== 'undefined' &&
  !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

/**
 * useNetwork — returns { isOnline, connectionType }
 * Works in both browser (navigator.onLine) and Capacitor native.
 */
export function useNetwork() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState('unknown');

  useEffect(() => {
    let cleanupCapacitor = null;

    const initCapacitorNetwork = async () => {
      try {
        const { Network } = await import('@capacitor/network');
        const status = await Network.getStatus();
        setIsOnline(status.connected);
        setConnectionType(status.connectionType);

        const handler = Network.addListener('networkStatusChange', (s) => {
          setIsOnline(s.connected);
          setConnectionType(s.connectionType);
        });
        cleanupCapacitor = () => handler.then((h) => h.remove());
      } catch {
        // fallback to browser API
      }
    };

    if (isCapacitor()) {
      initCapacitorNetwork();
    } else {
      const goOnline = () => setIsOnline(true);
      const goOffline = () => setIsOnline(false);
      window.addEventListener('online', goOnline);
      window.addEventListener('offline', goOffline);
      return () => {
        window.removeEventListener('online', goOnline);
        window.removeEventListener('offline', goOffline);
      };
    }

    return () => {
      if (cleanupCapacitor) cleanupCapacitor();
    };
  }, []);

  return { isOnline, connectionType };
}
